import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Produto } from '../../core/models/produto';
import { Marca } from '../../core/models/marca';
import { Fornecedor } from '../../core/models/fornecedor';
import { ProdutoService } from 'src/app/core/services/produto.service';
import { MarcaService } from 'src/app/core/services/marca.service';
import { FornecedorService } from 'src/app/core/services/fornecedor.service';
import { Garantia } from 'src/app/core/enums/garantia';
import { MessageService, ConfirmationService } from 'primeng/api';
import { AuthService } from '../../core/services/auth.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-produto',
  templateUrl: './produto.component.html',
  styleUrls: ['./produto.component.scss'],
})
export class ProdutoComponent implements OnInit, OnDestroy {
  produto = new Produto();
  marcas: Marca[] = [];
  fornecedores: Fornecedor[] = [];
  produtos: Produto[] = [];

  // edição
  editMode = false;
  editProdutoId: number | null = null;

  // ids para ligação com p-dropdown optionValue="id"
  selectedMarcaId: number | null = null;
  selectedFornecedorId: number | null = null;

  @ViewChild('produtoForm') produtoForm?: NgForm;

  garantiaOptions = Object.values(Garantia);

  private currentEmpresaId: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private readonly servico: ProdutoService,
    private readonly marcaService: MarcaService,
    private readonly fornecedorService: FornecedorService,
    private readonly messageService: MessageService,
    private readonly confirmation: ConfirmationService,
    private readonly authService: AuthService
  ) {}

  selecionar(): void {
    this.servico
      .selecionar()
      .subscribe((retorno) => (this.produtos = retorno));
  }

  cadastrar() {
    // ensure produto.marca/fornecedor reflect selected ids (same behavior as salvarAlteracao)
    if (this.selectedMarcaId != null) {
      const m = this.marcas.find((x) => Number(x.id) === Number(this.selectedMarcaId));
      this.produto.marca = m ?? ({ id: this.selectedMarcaId } as Marca);
    } else {
      this.produto.marca = null;
    }

    if (this.selectedFornecedorId != null) {
      const f = this.fornecedores.find((x) => Number(x.id) === Number(this.selectedFornecedorId));
      this.produto.fornecedor = f ?? ({ id: this.selectedFornecedorId } as Fornecedor);
    } else {
      this.produto.fornecedor = null;
    }

    this.servico.cadastrar(this.produto).subscribe({
      next: (ret) => {
        if (ret) this.produtos.push(ret);
        this.produto = new Produto(); // Reseta o formulário
        this.selectedMarcaId = null;
        this.selectedFornecedorId = null;
        if (this.produtoForm && typeof this.produtoForm.resetForm === 'function') {
          this.produtoForm.resetForm(this.produto);
        }
        this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Produto cadastrado.' });
      },
      error: (err) => {
        console.error('Erro ao cadastrar produto:', err);
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao cadastrar produto.' });
      }
    });
  }

  garantiaMap: { [key in Garantia]: string } = {
    'N_APLICAVEL': 'N/A',
    'SEM_GARANTIA': 'Sem garantia',
    'SETE_DIAS': '7 dias de garantia',
    'TRINTA_DIAS': '30 dias de garantia',
    'NOVENTA_DIAS': '90 dias de garantia',
    'SEIS_MESES': '6 meses de garantia',
    'UM_ANO': '1 ano de garantia',
    'UM_ANO_APPLE': '1 ano de garantia Apple',
    'GARANTIA_APPLE': 'Garantia Apple'
  };

  getGarantiaLabel(garantia: Garantia): string {
    return this.garantiaMap[garantia]; // Caso a garantia não seja reconhecida
  }

  editar(c: Produto): void {
    this.editMode = true;
    this.editProdutoId = c.id ?? null;

    this.produto = { ...c, marca: c.marca ? { ...c.marca } : null };
    // set selected ids so p-dropdown with optionValue="id" selects correctly
    const rawMarcaId = (c as any)?.marca?.id ?? (c as any).marcaId ?? (c as any).marca ?? null;
    this.selectedMarcaId = rawMarcaId != null ? Number(rawMarcaId) : null;
    const rawFornecedorId = (c as any)?.fornecedor?.id ?? (c as any).fornecedorId ?? (c as any).fornecedor ?? null;
    this.selectedFornecedorId = rawFornecedorId != null ? Number(rawFornecedorId) : null;

    // if the marcas/fornecedores list doesn't include the current id, add a placeholder so dropdown can show it
    if (this.selectedMarcaId != null && (!this.marcas || !this.marcas.find((m) => Number(m.id) === Number(this.selectedMarcaId)))) {
      if ((c as any)?.marca?.nome) {
        this.marcas = [{ id: this.selectedMarcaId, nome: (c as any).marca.nome } as Marca, ...this.marcas];
      } else {
        // add minimal placeholder
        this.marcas = [{ id: this.selectedMarcaId, nome: `#${this.selectedMarcaId}` } as Marca, ...this.marcas];
      }
    }

    if (this.selectedFornecedorId != null && (!this.fornecedores || !this.fornecedores.find((f) => Number(f.id) === Number(this.selectedFornecedorId)))) {
      if ((c as any)?.fornecedor?.nome) {
        this.fornecedores = [{ id: this.selectedFornecedorId, nome: (c as any).fornecedor.nome } as Fornecedor, ...this.fornecedores];
      } else {
        this.fornecedores = [{ id: this.selectedFornecedorId, nome: `#${this.selectedFornecedorId}` } as Fornecedor, ...this.fornecedores];
      }
    }
  }

  cancelarEdicao(): void {
    this.editMode = false;
    this.editProdutoId = null;
    this.produto = new Produto();
    this.selectedMarcaId = null;
    this.selectedFornecedorId = null;
    if (this.produtoForm && typeof this.produtoForm.resetForm === 'function') {
      this.produtoForm.resetForm(this.produto);
    }
  }

  salvarAlteracao(): void {
    if (!this.editMode || this.editProdutoId == null) return;
    // ensure produto.marca/fornecedor reflect selected ids
    if (this.selectedMarcaId != null) {
      const m = this.marcas.find((x) => Number(x.id) === Number(this.selectedMarcaId));
      this.produto.marca = m ?? ({ id: this.selectedMarcaId } as Marca);
    } else {
      this.produto.marca = null;
    }

    if (this.selectedFornecedorId != null) {
      const f = this.fornecedores.find((x) => Number(x.id) === Number(this.selectedFornecedorId));
      this.produto.fornecedor = f ?? ({ id: this.selectedFornecedorId } as Fornecedor);
    } else {
      this.produto.fornecedor = null;
    }
     this.servico.alterar(this.editProdutoId, this.produto).subscribe({
       next: (ret) => {
         const idx = this.produtos.findIndex((x) => x.id === this.editProdutoId);
         if (idx >= 0) this.produtos[idx] = ret;
         this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Produto alterado com sucesso.' });
         this.cancelarEdicao();
       },
       error: (err) => {
         this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao alterar produto.' });
         console.error('Erro ao alterar produto:', err);
       },
     });
   }

  confirmarAtivarDesativar(produto: Produto | undefined): void {
    if (!produto?.id) return;
    const willDeactivate = !!produto.ativo;
    const actionLabel = willDeactivate ? 'desativar' : 'ativar';

    this.confirmation.confirm({
      message: `Confirma ${actionLabel} o produto ${produto.nome || produto.id}?`,
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: () => {
        this.servico.ativarDesativar(produto.id as number, !willDeactivate).subscribe({
          next: (ret) => {
            const idx = this.produtos.findIndex((u) => u.id === produto.id);
            if (idx >= 0) {
              if (ret) this.produtos[idx] = ret;
              else this.produtos[idx].ativo = !willDeactivate;
            }
            this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: `Produto ${willDeactivate ? 'desativado' : 'ativado'} com sucesso.` });
          },
          error: (err) => {
            const msg = err?.error?.message || err?.message || `Erro ao ${actionLabel} produto`;
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: msg });
            console.error(`Erro ao ${actionLabel} produto:`, err);
          },
        });
      },
    });
  }

  ngOnInit() {
    this.currentEmpresaId = this.authService.getTenant();

    if (!this.currentEmpresaId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Tenant não configurado. Faça login novamente.',
      });
      return;
    }

    this.authService.currentTenant$
      .pipe(takeUntil(this.destroy$))
      .subscribe((tenantId) => {
        if (tenantId) {
          this.currentEmpresaId = tenantId;
          this.selecionar();
        }
      });

    this.marcaService.selecionar().subscribe((marcas: Marca[]) => {
      this.marcas = (marcas || []).map((m: any) => ({ ...m, id: m.id != null ? Number(m.id) : m.id }));
    });

    this.fornecedorService.selecionar().subscribe((fornecedores: Fornecedor[]) => {
      this.fornecedores = (fornecedores || []).map((f: any) => ({ ...f, id: f.id != null ? Number(f.id) : f.id }));
    });

    this.selecionar();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
